import { uid, TEAL } from "./constants";
import { textHeight } from "./textLayout";

const box   = (x, y, w, h, color, extra = {}) => ({ id: uid(), type: "rect", x, y, w, h, color, size: 2, ...extra });
const label = (x, y, w, text, color, size = 3) => ({ id: uid(), type: "text", x, y, w, h: 30, content: text, color, size });
const note  = (x, y, bg, text = "") => ({ id: uid(), type: "sticky", x, y, w: 130, h: 110, bg, content: text, color: "#1E293B", size: 2.4 });

const SLATE = "#64748B";

function brief(cx, cy) {
  const rows = [
    { key: "Ask",         hint: "What are we being asked for?",   color: "#3B82F6" },
    { key: "Opportunity", hint: "Why does it matter now?",        color: "#22C55E" },
    { key: "Challenge",   hint: "What makes it hard?",            color: "#F59E0B" },
    { key: "Solution",    hint: "What are we proposing?",         color: TEAL      },
  ];
  return rows.flatMap((r, i) => {
    const y = cy + i * 110;
    return [
      box(cx, y, 460, 92, r.color),
      label(cx + 14, y + 12, 240, r.key, r.color, 3.2),
      { ...label(cx + 14, y + 46, 430, r.hint, SLATE, 2.2), slot: "item" },
    ];
  });
}

function smart(cx, cy) {
  const rows = [
    { letter: "S", key: "Specific",   hint: "What exactly are we doing?" },
    { letter: "M", key: "Measurable", hint: "How do we know it worked?" },
    { letter: "A", key: "Achievable", hint: "Can we actually do this?" },
    { letter: "R", key: "Relevant",   hint: "Why is this worth doing?" },
    { letter: "T", key: "Time-bound", hint: "By when?" },
  ];
  return rows.flatMap((r, i) => {
    const y = cy + i * 92;
    return [
      box(cx, y, 470, 76, TEAL),
      label(cx + 16, y + 20, 40, r.letter, TEAL, 4),
      label(cx + 62, y + 12, 200, r.key, "#1E293B", 2.9),
      { ...label(cx + 62, y + 42, 380, r.hint, SLATE, 2.1), slot: "item" },
    ];
  });
}

function swot(cx, cy) {
  const cells = [
    { text: "S — Strengths",     dx: 0,   dy: 0,   color: "#22C55E" },
    { text: "W — Weaknesses",    dx: 220, dy: 0,   color: "#EF4444" },
    { text: "O — Opportunities", dx: 0,   dy: 170, color: "#3B82F6" },
    { text: "T — Threats",       dx: 220, dy: 170, color: "#F59E0B" },
  ];
  return cells.flatMap((c) => [
    box(cx + c.dx, cy + c.dy, 200, 150, c.color, { zone: cells.indexOf(c) }),
    label(cx + c.dx + 10, cy + c.dy + 14, 180, c.text, c.color),
  ]);
}

function prosCons(cx, cy) {
  return [
    box(cx, cy, 200, 260, "#22C55E", { zone: 0 }),
    label(cx + 10, cy + 14, 180, "+ Pros", "#22C55E", 4),
    box(cx + 220, cy, 200, 260, "#EF4444", { zone: 1 }),
    label(cx + 230, cy + 14, 180, "− Cons", "#EF4444", 4),
  ];
}

function mindMap(cx, cy) {
  const centerX = cx + 240;
  const centerY = cy + 160;
  const branches = [
    { dx: -220, dy: -130, text: "Branch 1" },
    { dx:  220, dy: -130, text: "Branch 2" },
    { dx: -220, dy:  130, text: "Branch 3" },
    { dx:  220, dy:  130, text: "Branch 4" },
  ];
  const rx = 80;
  const ry = 35;
  const spoke = (b) => {
    const ux = b.dx * 0.7;
    const uy = b.dy * 0.7;
    const t = 1 / Math.hypot(ux / rx, uy / ry);
    return {
      id: uid(), type: "line",
      x: Math.round(centerX + ux * t), y: Math.round(centerY + uy * t),
      w: Math.round(ux * (1 - t)), h: Math.round(uy * (1 - t)),
      color: "#94A3B8", size: 1.5,
    };
  };
  return [
    ...branches.map(spoke),
    { id: uid(), type: "circle", x: centerX - rx, y: centerY - ry, w: rx * 2, h: ry * 2, color: TEAL, size: 2 },
    { ...label(centerX - 60, centerY - 10, 120, "Central Idea", TEAL), slot: "topic" },
    ...branches.flatMap((b) => [
      box(centerX + b.dx - 70, centerY + b.dy - 25, 140, 50, SLATE, { size: 1.5 }),
      { ...label(centerX + b.dx - 55, centerY + b.dy - 8, 110, b.text, "#475569", 2.5), slot: "item" },
    ]),
  ];
}

function affinity(cx, cy) {
  const pile = ["", "", "", ""].map((_, i) =>
    note(cx, cy + 40 + i * 122, "#FEF08A")
  );
  const themes = [0, 1, 2].map((i) => {
    const x = cx + 190 + i * 210;
    return [
      box(x, cy, 190, 500, SLATE, { size: 1.5, dash: "8 6" }),
      { ...label(x + 12, cy + 12, 165, `Theme ${i + 1}`, SLATE, 2.6), slot: "item" },
    ];
  });
  return [
    label(cx, cy, 160, "Dump ideas", SLATE, 2.6),
    ...pile,
    ...themes.flat(),
  ];
}

function fishbone(cx, cy) {
  const spineY = cy + 150;
  const spineW = 520;
  const els = [
    { id: uid(), type: "arrow", x: cx, y: spineY, w: spineW, h: 0, color: "#475569", size: 2 },
    box(cx + spineW + 10, spineY - 34, 160, 68, "#EF4444"),
    { ...label(cx + spineW + 24, spineY - 12, 140, "Problem", "#EF4444", 3), slot: "topic" },
  ];
  const ribs = [
    { at: 140, up: true  }, { at: 340, up: true  },
    { at: 240, up: false }, { at: 440, up: false },
  ];
  ribs.forEach((r, i) => {
    const sx = cx + r.at;
    const dy = r.up ? -110 : 110;
    els.push({ id: uid(), type: "line", x: sx, y: spineY, w: -80, h: dy, color: "#94A3B8", size: 1.5 });
    els.push(box(sx - 150, spineY + dy + (r.up ? -34 : 0), 145, 40, SLATE, { size: 1.5 }));
    els.push({ ...label(sx - 138, spineY + dy + (r.up ? -24 : 10), 125, `Cause ${i + 1}`, "#475569", 2.3), slot: "item" });
  });
  return els;
}

function fiveWhys(cx, cy) {
  const els = [];
  ["Why 1?", "Why 2?", "Why 3?", "Why 4?", "Root Cause"].forEach((text, i) => {
    const isRoot = i === 4;
    const color = isRoot ? "#EF4444" : "#3B82F6";
    els.push(box(cx, cy + i * 90, 260, 70, color));
    els.push({ ...label(cx + 10, cy + i * 90 + 14, 240, text, color), slot: "item" });
    if (!isRoot) {
      els.push({ id: uid(), type: "arrow", x: cx + 130, y: cy + i * 90 + 70, w: 0, h: 20, color: "#94A3B8", size: 1.5 });
    }
  });
  return els;
}

function kanban(cx, cy) {
  const cols = [
    { text: "To Do",       color: "#94A3B8" },
    { text: "In Progress", color: "#F59E0B" },
    { text: "Done",        color: "#22C55E" },
  ];
  return cols.flatMap((c, i) => [
    box(cx + i * 220, cy, 200, 320, c.color, { zone: i }),
    label(cx + i * 220 + 10, cy + 12, 180, c.text, c.color, 3.5),
  ]);
}

export const FRAMEWORKS = {
  brief:      { label: "Brief",     build: brief },
  smart:      { label: "SMART",     build: smart },
  swot:       { label: "SWOT",      build: swot },
  "pros-cons":{ label: "Pros/Cons", build: prosCons },
  mindmap:    { label: "Mind Map",  build: mindMap },
  affinity:   { label: "Affinity",  build: affinity },
  fishbone:   { label: "Fishbone",  build: fishbone },
  "5why":     { label: "5 Whys",    build: fiveWhys },
  kanban:     { label: "Kanban",    build: kanban },
};

export function buildFramework(id, cx, cy) {
  const framework = FRAMEWORKS[id];
  if (!framework) return [];
  const groupId = uid();
  return framework.build(cx, cy).map((el) => ({ ...el, groupId }));
}

const cleanText = (value, max) => {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max - 1).trim()}…` : text;
};

export function fillFramework(elements, { title, items, groups } = {}) {
  const list = Array.isArray(items) ? items : [];
  let next = 0;
  const filled = elements.map((el) => {
    if (el.slot === "topic") {
      const text = cleanText(title, 60);
      return text ? { ...el, content: text } : el;
    }
    if (el.slot === "item") {
      const text = cleanText(list[next], 140);
      next += 1;
      return text ? { ...el, content: text } : el;
    }
    return el;
  });

  const lists = Array.isArray(groups) ? groups : [];
  const bullets = [];
  elements.forEach((el) => {
    if (!Number.isInteger(el.zone)) return;
    const points = (Array.isArray(lists[el.zone]) ? lists[el.zone] : [])
      .map((p) => cleanText(p, 48)).filter(Boolean);
    const fontSize = 12;
    const width = el.w - 24;
    let y = el.y + 46;
    for (const point of points) {
      const content = `• ${point}`;
      const h = Math.ceil(textHeight(content, width, fontSize)) + 2;
      if (y + h > el.y + el.h - 6) break;
      bullets.push({
        id: uid(), type: "text", x: el.x + 12, y, w: el.w - 24, h,
        content, color: "#334155", size: 2.3, groupId: el.groupId,
      });
      y += h + 4;
    }
  });
  return [...filled, ...bullets];
}

export function buildFilledFramework(id, cx, cy, spec) {
  return fillFramework(buildFramework(id, cx, cy), spec);
}

export const toolbarFrameworkTag = (frameworkId, groupId) => ({
  lumenSource: `fw-${groupId}`,
  lumenFramework: frameworkId,
  fromToolbar: true,
});

const NEAR = 2;
const near = (a, b) => Math.abs((a || 0) - (b || 0)) <= NEAR;
const SOLID = new Set(["rect", "circle"]);
const LOOSE = new Set(["text", "sticky"]);

function withCentreSpokes(els) {
  const hub = els.find((e) => e.type === "circle");
  if (!hub) return els;
  const cx = hub.x + hub.w / 2;
  const cy = hub.y + hub.h / 2;
  return els.map((e) => (e.type === "line"
    ? { ...e, x: cx, y: cy, w: e.x + e.w - cx, h: e.y + e.h - cy }
    : e));
}

let templateCache = null;
function templates() {
  if (!templateCache) {
    templateCache = Object.keys(FRAMEWORKS).flatMap((id) => {
      const els = FRAMEWORKS[id].build(0, 0);
      return id === "mindmap" ? [{ id, els }, { id, els: withCentreSpokes(els) }] : [{ id, els }];
    });
  }
  return templateCache;
}

const fits = (t, el, dx, dy) => t.type === el.type
  && near(el.x, t.x + dx) && near(el.y, t.y + dy)
  && (LOOSE.has(t.type) || (near(el.w, t.w) && near(el.h, t.h)));

function score(template, group) {
  const anchor = template.find((e) => SOLID.has(e.type));
  if (!anchor) return 0;
  let best = 0;
  for (const start of group) {
    if (start.type !== anchor.type || !near(start.w, anchor.w) || !near(start.h, anchor.h)) continue;
    const dx = start.x - anchor.x;
    const dy = start.y - anchor.y;
    const used = new Set();
    let hits = 0;
    for (const t of template) {
      const match = group.find((el) => !used.has(el.id) && fits(t, el, dx, dy));
      if (match) { used.add(match.id); hits += 1; }
    }
    best = Math.max(best, hits / template.length);
  }
  return best;
}

export function tagToolbarFrameworks(elements = []) {
  const groups = new Map();
  for (const el of elements) {
    if (!el.groupId || el.lumenSource) continue;
    if (!groups.has(el.groupId)) groups.set(el.groupId, []);
    groups.get(el.groupId).push(el);
  }
  const tags = new Map();
  for (const [groupId, group] of groups) {
    if (group.length < 3) continue;
    let found = null;
    let bestScore = 0.6;
    for (const { id, els } of templates()) {
      const s = score(els, group);
      if (s >= bestScore) { bestScore = s; found = id; }
    }
    if (found) tags.set(groupId, toolbarFrameworkTag(found, groupId));
  }
  if (!tags.size) return elements;
  return elements.map((el) => (!el.lumenSource && tags.has(el.groupId) ? { ...el, ...tags.get(el.groupId) } : el));
}
